import './OrderInformation.scss';
import { formatPrice } from "../../utils/FormatPrice";
import { formatRelativeDate, formatFullDate, formatListingDate } from "../../utils/FormatDate";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faHourglassStart,
  faLock,
  faReceipt,
  faStar,
  faCoins,
  faTags,
  faWallet,
  faMoneyBillTransfer,
  faCalendarDays,
} from '@fortawesome/free-solid-svg-icons';
import PaymentMethodBadge from './PaymentMethodBadge';
import RecordNav from '../RecordNav/RecordNav';

const statusMap = {
  COMPLETE: { icon: faCheckCircle,    label: 'Terminé',    variant: 'COMPLETE' },
  PENDING:  { icon: faHourglassStart, label: 'En attente', variant: 'PENDING'  },
  CANCELED: { icon: faLock,           label: 'Annulé',     variant: 'CANCELED' },
};

// neighbors / onNavigate (optional): previous/next sale arrows in the header (OrderDetails,
// via useRecordNeighbors).

// Same look as the product sheet's price / stock cards: brand-blue icon tile, amount with its
// currency small and gray. formatPrice ends with " <currency>" — split on that last regular
// space (the thousands separator is a narrow no-break space, never matched).
const BRAND_ICON_STYLE = { background: '#eff6ff', color: '#2563eb' };
const PriceWithUnit = ({ amount }) => {
  const full = formatPrice(amount);
  const cut = full.lastIndexOf(' ');
  if (cut < 0) return full;
  return <>{full.slice(0, cut)} <span className="order-total-unit">{full.slice(cut + 1)}</span></>;
};

const OrderInformation = ({ order, neighbors, onNavigate }) => {
  const status = statusMap[order.status];
  const variant = status?.variant || 'UNKNOWN';

  const hasDiscount = order.discountAmount !== null && order.discountAmount !== undefined;
  const hasLoyalty = order.loyaltyPointsUsed > 0 && order.loyaltyDiscountAmount > 0;
  const showRemaining = order.status === 'PENDING' && order.remainingAmount;
  const paymentMethods = [...new Set((order.invoices || []).map(inv => inv.paymentMethod).filter(Boolean))];

  return (
    <div className="order-info-wrapper">

      {/* ── Header ── */}
      <div className="order-header-card">
        <div className="order-header-body">
          <div className="order-avatar">
            <FontAwesomeIcon icon={faReceipt} style={{ fontSize: 20, color: '#2563eb' }} />
          </div>
          <div className="order-header-info">
            <p className="order-reference">{order.reference}</p>
            <p
              className="order-date"
            >
              <span data-tooltip-id="tooltip"
                data-tooltip-content={formatFullDate(order.createdAt)}>
                  {formatRelativeDate(order.createdAt)}
              </span>
            </p>
            <div className="order-badge-row">
              <span className={`order-status-badge order-status-badge--${variant}`}>
                {status?.icon && <FontAwesomeIcon icon={status.icon} />}
                {status?.label || 'Unknown'}
              </span>
            </div>
          </div>

          {onNavigate && <RecordNav neighbors={neighbors} onNavigate={onNavigate} noun="Vente" feminine />}
        </div>

        {/* ── Totaux ── */}
        <div className={`order-totals-grid${hasLoyalty ? ' order-totals-grid--with-loyalty' : ''} order-totals-grid--with-payment`}>
          {/* date of the sale: day + time, relative age underneath */}
          <div className="order-total-card">
            <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faCalendarDays} />
            </span>
            <div>
              <div className="order-total-label">Date</div>
              <div className="order-total-value order-total-value--date">{formatListingDate(order.createdAt)}</div>
              <div className="order-total-sub">{formatRelativeDate(order.createdAt)}</div>
            </div>
          </div>
          <div className="order-total-card">
            <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faCoins} />
            </span>
            <div>
              <div className="order-total-label">Sous-total</div>
              <div className="order-total-value"><PriceWithUnit amount={order.subtotal} /></div>
            </div>
          </div>
          <div className="order-total-card">
            <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faTags} />
            </span>
            <div>
              <div className="order-total-label">
                Remise {order.discountType === 'PERCENT' ? `(${order.discountValue}%)` : ''}
              </div>
              <div className="order-total-value order-total-value--discount">
                <PriceWithUnit amount={hasDiscount ? order.discountAmount : 0} />
              </div>
            </div>
          </div>
          {hasLoyalty && (
            <div className="order-total-card">
              <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
                <FontAwesomeIcon icon={faStar} />
              </span>
              <div>
                <div className="order-total-label">Points fidélité</div>
                <div className="order-total-value" style={{ color: '#b45309' }}>
                  -{formatPrice(order.loyaltyDiscountAmount)}
                  <span style={{ fontSize: 11, color: '#d97706', marginLeft: 4 }}>
                    ({order.loyaltyPointsUsed} pts)
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="order-total-card">
            <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faWallet} />
            </span>
            <div>
              <div className="order-total-label">Total</div>
              <div className="order-total-value order-total-value--total">
                <PriceWithUnit amount={order.total} />
              </div>
              {showRemaining && (
                <div className="order-total-sub order-total-value--remaining">
                  Reste : {formatPrice(order.remainingAmount)}
                </div>
              )}
            </div>
          </div>
          <div className="order-total-card">
            <span className="order-total-card__icon" style={BRAND_ICON_STYLE}>
              <FontAwesomeIcon icon={faMoneyBillTransfer} />
            </span>
            <div>
              <div className="order-total-label">Paiement</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 4 }}>
                {paymentMethods.length > 0
                  ? paymentMethods.map(m => <PaymentMethodBadge key={m} method={m} />)
                  : <span style={{ fontSize: 13, color: '#9ca3af' }}>—</span>}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderInformation;